import { Component } from '@theme/component';

/**
 * @typedef {Object} SizeChartsRefs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/**
 * Tabs for size-chart cards (Men / Women / Youth / Jackets).
 *
 * @extends {Component<SizeChartsRefs>}
 */
class SizeChartsComponent extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {number} */
  #active = 0;

  /**
   * @returns {HTMLButtonElement[]}
   */
  get #tabs() {
    const { tabs } = this.refs;
    return Array.isArray(tabs) ? tabs : tabs ? [tabs] : [];
  }

  /**
   * @returns {HTMLElement[]}
   */
  get #panels() {
    const { panels } = this.refs;
    return Array.isArray(panels) ? panels : panels ? [panels] : [];
  }

  connectedCallback() {
    super.connectedCallback();

    const { tablist } = this.refs;
    const tabs = this.#tabs;
    if (!tablist || !tabs.length) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    for (const [index, tab] of tabs.entries()) {
      tablist.append(tab);
      tab.addEventListener('click', (event) => this.handleTabSelect(index, event), { signal });
    }

    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.#activate(0, { focus: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * @param {number} index
   * @param {Event} [event]
   */
  handleTabSelect = (index, event) => {
    event?.preventDefault();
    this.#activate(Number(index), { focus: true });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const tabs = this.#tabs;
    const currentIndex = tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (currentIndex < 0) return;

    const lastIndex = tabs.length - 1;
    const keyMap = {
      ArrowLeft: currentIndex - 1,
      ArrowRight: currentIndex + 1,
      Home: 0,
      End: lastIndex,
    };
    const nextIndex = keyMap[event.key];

    if (nextIndex === undefined) return;

    event.preventDefault();
    const wrapped = (nextIndex + tabs.length) % tabs.length;
    this.#activate(wrapped, { focus: true });
  };

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #activate(index, options = {}) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    if (!tabs.length) return;

    const nextIndex = Math.max(0, Math.min(index, tabs.length - 1));
    this.#active = nextIndex;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === nextIndex;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    }

    for (const [i, panel] of panels.entries()) {
      const selected = i === nextIndex;
      const chart = panel.closest('.size-chart');

      panel.toggleAttribute('hidden', !selected);
      panel.toggleAttribute('inert', !selected);
      chart?.toggleAttribute('hidden', !selected);
      chart?.closest('.shopify-block')?.toggleAttribute('hidden', !selected);
    }

    if (options.focus) {
      tabs[nextIndex]?.focus({ preventScroll: true });
      this.#pinToTop();
    }
  }

  #pinToTop() {
    const offset = Number.parseFloat(getComputedStyle(this).scrollMarginBlockStart) || 0;

    if (Math.ceil(this.getBoundingClientRect().top) >= offset) return;

    this.scrollIntoView({ block: 'start', behavior: 'auto' });
  }
}

if (!customElements.get('size-charts-component')) {
  customElements.define('size-charts-component', SizeChartsComponent);
}
